/* istanbul ignore file */
import BIMESSAGES from 'core/data-model/constants/bi-messages'
import VALUE from 'core/data-model/constants/values'
import { createPdfDocument } from 'core/service/pdf-generation/document-creation'
import { Footer } from 'core/service/pdf-generation/products/common/components/general'
import { getServiceWrapper } from 'core/service-wrapper'

import { rpudrContent } from './products/rpudr/content'
import {
  BenefitTableExampleGroup,
  CashflowChart,
  Cover,
  FeeDetails,
  FormulaDetails,
  MutualFundPortfolio,
  PaymentChannelsILink,
  ProcessingTimeline,
  Recommendation,
  RightsInsured,
  RPULConditionsV2,
  RPULConditions2,
  Sign,
  SPULConditions1,
  SPULConditions2,
} from './sections'

// template
const pageOrientation = 'landscape'
const defaultStyle = {
  font: 'PSLTextPro',
  fontSize: 14,
}

const iWealthyContent = async (props) => {
  const serviceWrapper = getServiceWrapper()
  const paymentChannels = PaymentChannelsILink()
  const portfolio = await MutualFundPortfolio(props)

  const {
    displayProduct,
    regularPremium,
    regularTopUp,
    lumpSum,
    age,
    gender,
    paymentInterval,
    sumAssured,
  } = props.cashFlowProps

  const isUseNewRate = await serviceWrapper.products.INVESTMENT.isUsedCoiTMORate()
  const cashFlowByExpectedReturn = await serviceWrapper.products.INVESTMENT.generateCashFlowByExpectedReturn(
    displayProduct,
    regularPremium,
    regularTopUp,
    lumpSum,
    age,
    gender,
    paymentInterval,
    sumAssured,
    [-1, 2, 5]
  )
  const propWithCashFlowByExpectedReturn = { ...props, cashFlowByExpectedReturn }
  const rateYear = isUseNewRate ? VALUE.COI_RATE_YEAR_CURRENT : VALUE.COI_RATE_YEAR_BEFORE

  return [
    Cover({
      ...propWithCashFlowByExpectedReturn,
      unitLinkedMessage: BIMESSAGES.IWEALTHY_UNIT_LINKED,
      unitLinkedThMessage: BIMESSAGES.IWEALTHY_UNIT_LINKED_TH,
    }),
    CashflowChart(props.productCode, propWithCashFlowByExpectedReturn),
    portfolio,
    BenefitTableExampleGroup(propWithCashFlowByExpectedReturn),
    FormulaDetails(props.productCode, rateYear),
    RPULConditionsV2(),
    RPULConditions2(),
    ProcessingTimeline(props.productCode),
    FeeDetails(props.productCode, rateYear),
    paymentChannels,
    RightsInsured(props.productCode),
    Recommendation(props.productCode),
    Sign(propWithCashFlowByExpectedReturn),
  ]
}

const iInvestContent = async (props) => {
  const serviceWrapper = getServiceWrapper()
  const paymentChannels = await PaymentChannelsILink()
  const portfolio = await MutualFundPortfolio(props)

  const {
    displayProduct,
    regularPremium,
    regularTopUp,
    lumpSum,
    age,
    gender,
    paymentInterval,
    sumAssured,
  } = props.cashFlowProps

  const cashFlowByExpectedReturn = await serviceWrapper.products.INVESTMENT.generateCashFlowByExpectedReturn(
    displayProduct,
    regularPremium,
    regularTopUp,
    lumpSum,
    age,
    gender,
    paymentInterval,
    sumAssured,
    [-1, 2, 5]
  )
  const propWithCashFlowByExpectedReturn = { ...props, cashFlowByExpectedReturn }

  return [
    Cover({
      ...props,
      unitLinkedMessage: BIMESSAGES.IINVEST_UNIT_LINKED,
      unitLinkedThMessage: BIMESSAGES.IINVEST_UNIT_LINKED_TH,
    }),
    CashflowChart(props.productCode, propWithCashFlowByExpectedReturn),
    portfolio,
    BenefitTableExampleGroup(propWithCashFlowByExpectedReturn),
    FormulaDetails(props.productCode, ''),
    SPULConditions1(),
    SPULConditions2(),
    ProcessingTimeline(props.productCode),
    FeeDetails(props.productCode, ''),
    paymentChannels,
    RightsInsured(props.productCode),
    Recommendation(props.productCode),
    Sign(props),
  ]
}

async function getContentByProductCode(props) {
  switch (props.productCode) {
    case BIMESSAGES.BI_TYPE_PRODUCT_CODE_IWEALTHY:
      return await iWealthyContent(props)
    case BIMESSAGES.BI_TYPE_PRODUCT_CODE_IINVEST:
      return await iInvestContent(props)
    case BIMESSAGES.BI_TYPE_PRODUCT_CODE_RPUDR:
      return await rpudrContent(props)
    default:
      return null
  }
}

export const createDocDefinition = async (props) => {
  const content = await getContentByProductCode(props)
  return {
    content,
    defaultStyle,
    styles: {
      tableHeader: { fillColor: VALUE.HARD_BLUE, color: 'white', alignment: 'center' },
      center: { alignment: 'center' },
    },
    pageOrientation,
    footer: Footer(props.productCode),
  }
}

export const createInvestmentPdf = createPdfDocument(createDocDefinition)

/**
 * createProductInvestmentOdfDocumentFunc
 * @param investmentCashFlowService {ReturnType<import('core/service/investment/cash-flow/cash-flow').createInvestmentCashFlowService>}
 */
export function createProductInvestmentPdfDocumentFunc(investmentCashFlowService) {
  const _generateCashFlowByExpectedReturn = async (
    displayProduct,
    regularPremium,
    regularTopUp,
    lumpSum,
    age,
    gender,
    paymentInterval,
    sumAssured,
    percentages
  ) => {
    const generateCashFlowByExpectedReturn = (expectedReturn) =>
      investmentCashFlowService.generateCashFlow(
        displayProduct,
        expectedReturn,
        regularPremium,
        regularTopUp,
        lumpSum,
        age,
        gender,
        paymentInterval,
        sumAssured
      )

    let cashFlowByExpectedReturn = {}
    for (let expectedReturn of percentages) {
      cashFlowByExpectedReturn[`${expectedReturn}`] = await generateCashFlowByExpectedReturn(expectedReturn)
    }

    return cashFlowByExpectedReturn
  }

  return {
    _generateCashFlowByExpectedReturn,
  }
}
